import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Boxes } from 'lucide-react';
import { useActiveAppId, useSelectedAppName, isCoreAppId } from '@/hooks/useSelectedAppId';

export interface AppScopeBannerProps {
  /** When true, show a warning if no app is selected (default). */
  requireApp?: boolean;
  className?: string;
}

/**
 * Shows which app instance data is scoped to, or warns when none is selected.
 * AppSwitcher in the header is the selection control — this is read-only context.
 */
export function AppScopeBanner({ requireApp = true, className = '' }: AppScopeBannerProps) {
  const navigate = useNavigate();
  const activeAppId = useActiveAppId();
  const appName = useSelectedAppName();
  const isCore = isCoreAppId(activeAppId, appName);

  if (!requireApp) return null;

  if (activeAppId) {
    return (
      <div
        className={`flex items-center gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2 ${className}`}
      >
        <Boxes className="w-3.5 h-3.5 shrink-0" />
        <span>
          {isCore ? (
            <>
              Platform catalog: <strong>{appName || 'Dev Cockpit Core'}</strong>
              <span className="text-indigo-600 font-mono ml-1">({activeAppId})</span>
              <span className="text-indigo-500 ml-1">— all templates</span>
            </>
          ) : (
            <>
              App scope: <strong>{appName || activeAppId}</strong>
              <span className="text-indigo-600 font-mono ml-1">({activeAppId})</span>
            </>
          )}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-between gap-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 ${className}`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span>Select an app in the header to view app-scoped data.</span>
      </div>
      <button
        type="button"
        onClick={() => navigate('/apps')}
        className="shrink-0 underline hover:no-underline"
      >
        Manage apps
      </button>
    </div>
  );
}

/** Blocks children until an app is selected; shows banner instead. */
export function AppScopeGate({ children }: { children: React.ReactNode }) {
  const appId = useActiveAppId();
  if (!appId) {
    return (
      <div className="p-4 space-y-4">
        <AppScopeBanner />
      </div>
    );
  }
  return <>{children}</>;
}
